import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthenticationService } from '../../services/auth/authentication.service';
import { ApiService } from '../../services/api/api.service';
import { FormatService } from '../../services/format/format.service';
import * as moment from 'moment';

// Read-only detail of one USER's Muraja'ah: hero + stat strip, then their
// Muraja'ah list (Juz / Surat / Halaman items), the per-juz rotation map,
// weak pages, and their session history.
@Component({
  selector: 'app-murojaah-detail',
  templateUrl: './murojaah-detail.component.html',
  styleUrls: ['./murojaah-detail.component.css']
})
export class MurojaahDetailComponent implements OnInit, OnDestroy {
  id : number = 0;
  // identity for the hero — the muraja'ah API carries no user fields, so it
  // comes from (1) the list row that navigated here, (2) the Tahfidz user
  // endpoint when this admin may read it, or (3) the first session row
  user : any = null;
  directoryFile : string = '';
  pageSize : number = 10;
  loading : any = {};

  mrj : any = null;
  mSessions : any = [];
  mSessionsTotal : number = 0;
  mSessionsPage : number = 1;
  mSessionsPages : any = [];

  isViewer : string = '';
  private routeSub : Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthenticationService,
    private apiService: ApiService,
    private formatService: FormatService,
  ) { }

  ngOnInit(): void {
    moment.locale('id');
    this.pageSize = this.apiService.LIMIT || 10;
    this.routeSub = this.route.paramMap.subscribe(params => {
      this.id = Number(params.get('id')) || 0;
      this.mrj = null; this.mSessions = []; this.mSessionsTotal = 0; this.mSessionsPage = 1;
      this.user = null;
      const passed = (history.state && history.state.user) || null;
      if(passed && passed.name){
        this.setUser(passed.name, passed.email, passed.photoUrl);
      }else if(this.authService.hasMenu('hafalan')){
        this.loadUserHeader();
      }
      this.loadMurojaah();
      this.loadMurojaahSessions();
    });
  }
  ngOnDestroy(): void {
    if(this.routeSub){ this.routeSub.unsubscribe(); }
    document.body.style.overflow = '';
  }

  private setUser(name : any, email : any, photoUrl : any){
    this.user = {nameVw: name || 'Akun tidak ditemukan', email: email || '', photoUrl: photoUrl || ''};
  }
  // only called when the admin also holds the Tahfidz permission
  loadUserHeader(){
    this.loading.user = true;
    this.apiService.methodGetSilent(`hafalan/user/${this.id}`, this.authService.getToken()).subscribe((response) => {
      this.loading.user = false;
      if(response && response.status == 200 && response.data){
        this.directoryFile = response.directoryFile || this.directoryFile;
        const u = response.data;
        this.setUser(u.name, u.email, this.photoUrl(u.photo, 'user/avatar/'));
      }else{ this.userFromSessions(); }
    }, () => { this.loading.user = false; this.userFromSessions(); });
  }
  // last-resort identity: every session row carries its owner's name/photo
  private userFromSessions(){
    const first = this.mSessions[0];
    if(!this.user && !this.loading.user && first && first.user_name){
      this.setUser(first.user_name, '', this.photoUrl(first.user_photo, 'user/avatar/'));
    }
  }

  photoUrl(photo : any, folder : string){
    if(!photo){ return ''; }
    return String(photo).includes('http') ? photo : `${this.directoryFile}${folder}${photo}`;
  }
  dayLabel(day : any){
    if(day == null){ return ''; }
    return moment.utc(Number(day) * 86400000).format('D MMM YYYY');
  }
  back(){ this.router.navigate(['/murojaah']); }

  loadMurojaah(){
    this.loading.murojaah = true;
    this.apiService.methodGetSilent(`murojaah/user/${this.id}`, this.authService.getToken()).subscribe((response) => {
      this.loading.murojaah = false;
      if(response && response.status == 200 && response.data){
        const m = response.data;
        if(m.available){
          const plan = m.plan;
          m.planVw = !plan ? '' : !plan.active ? 'Tanpa jadwal'
            : plan.kind === 3 ? '7 manzil / pekan' : `${this.formatNumber(plan.amount)} ${plan.kind_label}`;
          const graded = m.lancar_30d + m.tersangkut_30d + m.lupa_30d;
          m.lancarPct = graded ? Math.round(m.lancar_30d / graded * 100) : null;
          m.items = (m.items || []).map((it : any) => {
            it.lastReviewedVw = it.last_reviewed_ago == null ? 'Belum pernah'
              : it.last_reviewed_ago <= 0 ? 'Hari ini' : `${this.formatNumber(it.last_reviewed_ago)} hari lalu`;
            it.lastReviewedFull = this.dayLabel(it.last_reviewed_day);
            return it;
          });
          m.itemsJuz = m.items.filter((it : any) => it.kind === 'juz').length;
          m.itemsSurah = m.items.filter((it : any) => it.kind === 'surah').length;
          m.itemsPages = m.items.filter((it : any) => it.kind === 'pages').length;
          (m.juz || []).forEach((j : any) => {
            const memorized = j.hafal_pages + j.partial_pages;
            j.title = memorized
              ? `Juz ${j.juz}: ${j.fresh_pages}/${memorized} halaman sudah diulang`
                + (j.weak_pages ? ` · ${j.weak_pages} lemah` : '')
                + (j.last_reviewed_ago == null ? ' · belum pernah' : ` · terakhir ${this.agoLabel(j.last_reviewed_ago)}`)
              : `Juz ${j.juz}: belum ada hafalan`;
          });
          m.weak = m.weak || [];
          m.weak.forEach((w : any) => {
            w.agoVw = w.last_reviewed_ago == null ? '—' : this.agoLabel(w.last_reviewed_ago);
          });
        }
        this.mrj = m;
      }
    }, () => { this.loading.murojaah = false; });
  }
  loadMurojaahSessions(){
    this.loading.mSessions = true;
    const offset = (this.mSessionsPage - 1) * this.pageSize;
    this.apiService.methodGetSilent(`murojaah/user/${this.id}/sessions/${offset}`, this.authService.getToken()).subscribe((response) => {
      this.loading.mSessions = false;
      if(response && response.status == 200){
        this.directoryFile = response.directoryFile || this.directoryFile;
        this.mSessions = (response.data || []).map((s : any) => {
          s.juzVw = s.juz && s.juz.length ? 'Juz ' + s.juz.join(', ') : '';
          s.timeVw = s.finished_at ? moment(s.finished_at).fromNow() : '';
          s.timeFull = s.finished_at ? moment(s.finished_at).format('D MMMM YYYY HH:mm') : '';
          s.dayVw = s.finished_at ? moment(s.finished_at).format('D MMM YYYY, HH:mm') : this.dayLabel(s.day);
          s.durationVw = s.duration_min == null ? '' : s.duration_min < 1 ? '< 1 mnt' : `${this.formatNumber(s.duration_min)} mnt`;
          s.isClean = !s.tersangkut_count && !s.lupa_count;
          return s;
        });
        this.userFromSessions();
        this.mSessionsTotal = response.total || 0;
        this.mSessionsPages = this.pagesFor(this.mSessionsPage, this.pagesOf(this.mSessionsTotal));
      }
    }, () => { this.loading.mSessions = false; });
  }
  goMurojaahSessionsPage(p : any){
    if(p < 1 || p > this.pagesOf(this.mSessionsTotal) || p == this.mSessionsPage){ return; }
    this.mSessionsPage = p; this.loadMurojaahSessions();
  }
  agoLabel(days : number){
    if(days <= 0){ return 'hari ini'; }
    if(days === 1){ return 'kemarin'; }
    return `${this.formatNumber(days)} hari lalu`;
  }

  pagesOf(total : number){ return Math.max(1, Math.ceil(total / this.pageSize)); }
  pagesFor(current : number, total : number){
    const pages: any[] = [];
    for(let p = 1; p <= total; p++){
      if(p == 1 || p == total || Math.abs(p - current) <= 1){ pages.push(p); }
      else if(pages[pages.length - 1] != '...'){ pages.push('...'); }
    }
    return pages;
  }
  rangeFrom(page : number, total : number){ return total ? (page - 1) * this.pageSize + 1 : 0; }
  rangeTo(page : number, len : number){ return (page - 1) * this.pageSize + len; }
  formatNumber(value : any){ return this.formatService.dotFormat(value || 0); }

  openViewer(url : string){
    if(!url){ return; }
    this.isViewer = url;
    document.body.style.overflow = 'hidden';
  }
  closeViewer(){
    this.isViewer = '';
    document.body.style.overflow = '';
  }
  @HostListener('document:keydown.escape')
  onEscape(){ if(this.isViewer){ this.closeViewer(); } }
}
